import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  ActivityIndicator,
  FlatList,
  RefreshControl,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, ConfirmationModal, FloatingActionButton } from '../components';
import { EventCard } from '../components/calendar';
import { apiService } from '../services/api';
import { cancelLocalReminders } from '../services/calendar-local-reminders.service';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { useAppStore } from '../store/appStore';
import { useLocale } from '../contexts/LocaleContext';
import { colors, spacing, typography } from '../theme';
import { useThemedStyles } from '../hooks/useThemedStyles';

interface EventListScreenProps {
  calendarId?: string;
}

export const EventListScreen: React.FC<EventListScreenProps> = () => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, showNotification, routeParams, activeTab, setActiveTab } = useAppContext();
  const { currentBusiness } = useAppStore();
  const { t } = useLocale();
  const [events, setEvents] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [calendarId, setCalendarId] = useState<string | null>(
    routeParams?.calendarId || null
  );
  const [calendar, setCalendar] = useState<any>(null);

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const loadCalendar = useCallback(async () => {
    if (!calendarId || !currentBusiness?._id) return;

    try {
      const response = await apiService.getCalendarById(calendarId, currentBusiness._id);
      if (response.statusCode === 1000 && response.data) {
        setCalendar(response.data);
      }
    } catch (error: any) {
      console.error('Error loading calendar:', error);
    }
  }, [calendarId, currentBusiness]);

  const loadEvents = useCallback(async () => {
    if (!calendarId || !currentBusiness?._id) {
      setError(t('calendar.eventList.noSelection'));
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const response = await apiService.getEventsByCalendar(calendarId, currentBusiness._id);

      if (response.statusCode === 1000 && response.data) {
        setEvents(response.data || []);
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.eventList.loadFailed');
        setError(errorMessage);
        showNotification('error', 'Error', errorMessage);
      }
    } catch (error: any) {
      console.error('Error loading events:', error);
      const errorMessage = error.message || t('calendar.eventList.loadFailed');
      setError(errorMessage);
      showNotification('error', 'Error', errorMessage);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [calendarId, currentBusiness, showNotification]);

  useEffect(() => {
    if (calendarId) {
      loadCalendar();
      loadEvents();
    }
  }, [calendarId, loadCalendar, loadEvents]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadEvents();
  };

  const handleEventPress = (eventId: string) => {
    navigateTo('event-detail', eventId);
  };

  const handleEventEdit = (eventId: string) => {
    navigateTo('event-edit', eventId);
  };

  const handleEventDelete = (eventId: string) => {
    setEventToDelete(eventId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!eventToDelete || !currentBusiness?._id) return;

    try {
      setDeleting(true);
      const response = await apiService.deleteEvent(eventToDelete, currentBusiness._id);
      if (response.statusCode === 1000) {
        showNotification('success', 'Success', t('calendar.eventList.deleteSuccess'));
        cancelLocalReminders(eventToDelete).catch((err) =>
          console.error('[EventListScreen] Failed to cancel local reminders:', err)
        );
        setShowDeleteModal(false);
        setEventToDelete(null);
        loadEvents();
      } else {
        const errorMessage = response.errors?.[0]?.message || t('calendar.eventList.deleteFailed');
        showNotification('error', 'Error', errorMessage);
        setShowDeleteModal(false);
        setEventToDelete(null);
      }
    } catch (error: any) {
      showNotification('error', 'Error', error.message || t('calendar.eventList.deleteFailed'));
      setShowDeleteModal(false);
      setEventToDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateEvent = () => {
    if (calendarId) {
      navigateTo('event-create', calendarId);
    }
  };

  const renderEvent = ({ item }: { item: any }) => (
    <EventCard
      event={item}
      onPress={handleEventPress}
      onEdit={handleEventEdit}
      onDelete={handleEventDelete}
      showActions={true}
    />
  );

  if (loading && events.length === 0) {
    return (
      <ScreenLayout
        header={
          <Header
            showNotifications={true}
            showMessages={true}
            onNotificationPress={() => navigateTo('notifications')}
            onMessagePress={() => navigateTo('chat')}
            showNavigation={Platform.OS === 'web'}
            activeTab={activeTab}
            onTabPress={handleTabPress}
          />
        }
        showBottomTabs={Platform.OS !== 'web'}
        onTabPress={handleTabPress}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
        rightPaneContent={null}
      >
        <View style={styles.headerContainer}>
          <Header title={calendar?.name || t('calendar.eventList.fallbackTitle')} showBackButton onBackPress={() => navigateTo('calendars')} />
        </View>
        <View style={styles.centerContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text style={styles.centerText}>{t('calendar.eventList.loading')}</Text>
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onTabPress={handleTabPress}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      rightPaneContent={null}
    >
      <View style={styles.headerContainer}>
        <Header
          title={calendar?.name || t('calendar.eventList.fallbackTitle')}
          showBackButton
          onBackPress={() => navigateTo('calendars')}
        />
      </View>

      {error && events.length === 0 ? (
        <View style={styles.centerContainer}>
          <Ionicons name="alert-circle-outline" size={64} color={colors.error} />
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity style={styles.retryButton} onPress={loadEvents}>
            <Text style={styles.retryButtonText}>{t('calendar.eventList.retry')}</Text>
          </TouchableOpacity>
        </View>
      ) : events.length === 0 ? (
        <View style={styles.centerContainer}>
          <Ionicons name="calendar-outline" size={64} color={colors.textLight} />
          <Text style={styles.emptyText}>{t('calendar.eventList.noEventsFound')}</Text>
          <Text style={styles.emptySubtext}>
            {t('calendar.eventList.createFirstEvent')}
          </Text>
        </View>
      ) : (
        <FlatList
          data={events}
          renderItem={renderEvent}
          keyExtractor={(item, index) => item._id || `event-${index}`}
          contentContainerStyle={styles.listContent}
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={handleRefresh}
              colors={[colors.primary]}
              tintColor={colors.primary}
            />
          }
          showsVerticalScrollIndicator={false}
        />
      )}

      {calendarId && (
        <FloatingActionButton
          options={[
            {
              id: 'create-event',
              label: t('calendar.eventList.createEvent'),
              icon: 'add',
              color: colors.primary,
              onPress: handleCreateEvent,
            },
          ]}
          mainIcon="add"
          mainColor={colors.primary}
        />
      )}

      <ConfirmationModal
        visible={showDeleteModal}
        title={t('calendar.eventList.deleteTitle')}
        message={t('calendar.eventList.deleteMessage')}
        confirmLabel={t('calendar.eventList.deleteConfirm')}
        cancelLabel={t('calendar.eventList.deleteCancel')}
        onConfirm={confirmDelete}
        onCancel={() => {
          setShowDeleteModal(false);
          setEventToDelete(null);
        }}
        loading={deleting}
        variant="danger"
      />
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  centerContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.xl,
  },
  centerText: {
    ...typography.body,
    color: colors.textLight,
    marginTop: spacing.md,
  },
  errorText: {
    ...typography.body,
    color: colors.error,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  emptyText: {
    ...typography.h3,
    color: colors.textLight,
    marginTop: spacing.md,
    textAlign: 'center',
  },
  emptySubtext: {
    ...typography.body,
    color: colors.textLight,
    marginTop: spacing.xs,
    textAlign: 'center',
  },
  retryButton: {
    marginTop: spacing.md,
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.sm,
    backgroundColor: colors.primary,
    borderRadius: 8,
  },
  retryButtonText: {
    ...typography.body,
    color: colors.white,
    fontWeight: '600',
  },
  listContent: {
    padding: spacing.md,
  },
  headerContainer: {
    marginBottom: spacing.md,
  },
});

